import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import converter from 'number-to-words';
import axios from 'axios';
import { baseUrl } from '../../../baseUrl';
import { generateAndDownloadBill } from './generateAndDownloadMessbillManual';

const MessBillGenerator = () => {
  const [formData, setFormData] = useState({
    hostel_admission_no: '',
    admission_no: '',
    name: '',
    hostel: '',
    period_type: 'single', // 'single' or 'range'
    month: '',
    year: '',
    from_month: '',
    from_year: '',
    to_month: '',
    to_year: '',
    amount: '',
    rent_paid: '',
    fine_paid: ''
  });

  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.hostel_admission_no.trim()) {
      newErrors.hostel_admission_no = 'Hostel Admission No is required';
    }
    
    if (!formData.admission_no.trim()) {
      newErrors.admission_no = 'Admission No is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.hostel) {
      newErrors.hostel = 'Hostel selection is required';
    }
    
    // Validate period based on type
    if (formData.period_type === 'single') {
      if (!formData.month) {
        newErrors.month = 'Month is required';
      }
      if (!formData.year) {
        newErrors.year = 'Year is required';
      }
    } else if (formData.period_type === 'range') {
      if (!formData.from_month) {
        newErrors.from_month = 'From month is required';
      }
      if (!formData.from_year) {
        newErrors.from_year = 'From year is required';
      }
      if (!formData.to_month) {
        newErrors.to_month = 'To month is required';
      }
      if (!formData.to_year) {
        newErrors.to_year = 'To year is required';
      }
      
      // Validate that "to" date is not before "from" date
      if (formData.from_month && formData.from_year && formData.to_month && formData.to_year) {
        const fromDate = new Date(formData.from_year, formData.from_month - 1);
        const toDate = new Date(formData.to_year, formData.to_month - 1);
        
        if (toDate < fromDate) {
          newErrors.to_month = 'To date cannot be before From date';
          newErrors.to_year = 'To date cannot be before From date';
        }
      }
    }
    
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Valid mess bill amount is required';
    }
    
    if (!formData.rent_paid || formData.rent_paid < 0) {
      newErrors.rent_paid = 'Valid rent amount is required';
    }
    
    if (!formData.fine_paid || formData.fine_paid < 0) {
      newErrors.fine_paid = 'Valid fine amount is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsGenerating(true);
    
    try {
      // Prepare data for backend based on period type
      let backendData = { hostel: formData.hostel };
      
      if (formData.period_type === 'single') {
        backendData.month = formData.month;
        backendData.year = formData.year;
      } else {
        backendData.from_month = formData.from_month;
        backendData.from_year = formData.from_year;
        backendData.to_month = formData.to_month;
        backendData.to_year = formData.to_year;
      }

      // Generate receipt number
      const res = await axios.post(`${baseUrl}/warden/generate-receipt-no`, backendData);
    
      const receiptNo = res.data.receipt_no;
      
      // Pass the data to the PDF generator
      await generateAndDownloadBill({
        ...formData,
        receipt_no: receiptNo
      });
      
      // Reset form after successful generation
      setFormData({
        hostel_admission_no: '',
        admission_no: '',
        name: '',
        hostel: '',
        period_type: 'single',
        month: '',
        year: '',
        from_month: '',
        from_year: '',
        to_month: '',
        to_year: '',
        amount: '',
        rent_paid: '',
        fine_paid: ''
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getMonthName = (monthValue) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthValue - 1];
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const hostels = [
    { value: 'MH', label: 'MH - Men\'s Hostel' },
    { value: 'LH', label: 'LH - Ladies\' Hostel' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="min-h-screen w-11/12 p-4">
      <div className="max-w-5xl mx-auto">
        <div className="rounded-xl p-6 md:p-8">
          <div className="text-left mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Mess Bill Generator</h1>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hostel Admission No
                </label>
                <input
                  type="text"
                  name="hostel_admission_no"
                  value={formData.hostel_admission_no}
                  onChange={handleChange}
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.hostel_admission_no 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter hostel admission number"
                />
                {errors.hostel_admission_no && (
                  <p className="text-red-600 text-sm mt-1">{errors.hostel_admission_no}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  College Admission No
                </label>
                <input
                  type="text"
                  name="admission_no"
                  value={formData.admission_no}
                  onChange={handleChange}
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.admission_no 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter college admission number"
                />
                {errors.admission_no && (
                  <p className="text-red-600 text-sm mt-1">{errors.admission_no}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Student Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.name 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter student name"
                />
                {errors.name && (
                  <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hostel
                </label>
                <select
                  name="hostel"
                  value={formData.hostel}
                  onChange={handleChange}
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.hostel 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                >
                  <option value="">Select hostel</option>
                  {hostels.map(hostel => (
                    <option key={hostel.value} value={hostel.value}>
                      {hostel.label}
                    </option>
                  ))}
                </select>
                {errors.hostel && (
                  <p className="text-red-600 text-sm mt-1">{errors.hostel}</p>
                )}
              </div>

              {/* Period Selection Section */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Bill Period</h3>
                
                {/* Period Type Selection */}
                <div className="mb-4">
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="period_type"
                        value="single"
                        checked={formData.period_type === 'single'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Single Month</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="period_type"
                        value="range"
                        checked={formData.period_type === 'range'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Date Range</span>
                    </label>
                  </div>
                </div>

                {/* Single Month/Year Selection */}
                {formData.period_type === 'single' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Month
                      </label>
                      <select
                        name="month"
                        value={formData.month}
                        onChange={handleChange}
                        className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                          errors.month 
                            ? 'ring-red-300 focus:ring-red-500' 
                            : 'ring-slate-300 focus:ring-blue-500'
                        }`}
                      >
                        <option value="">Select month</option>
                        {months.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      {errors.month && (
                        <p className="text-red-600 text-sm mt-1">{errors.month}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year
                      </label>
                      <select
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                          errors.year 
                            ? 'ring-red-300 focus:ring-red-500' 
                            : 'ring-slate-300 focus:ring-blue-500'
                        }`}
                      >
                        <option value="">Select year</option>
                        {years.map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                      {errors.year && (
                        <p className="text-red-600 text-sm mt-1">{errors.year}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Date Range Selection */}
                {formData.period_type === 'range' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* From Period */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-600">From</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Month
                          </label>
                          <select
                            name="from_month"
                            value={formData.from_month}
                            onChange={handleChange}
                            className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                              errors.from_month 
                                ? 'ring-red-300 focus:ring-red-500' 
                                : 'ring-slate-300 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select month</option>
                            {months.map(month => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                          {errors.from_month && (
                            <p className="text-red-600 text-sm mt-1">{errors.from_month}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Year
                          </label>
                          <select
                            name="from_year"
                            value={formData.from_year}
                            onChange={handleChange}
                            className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                              errors.from_year 
                                ? 'ring-red-300 focus:ring-red-500' 
                                : 'ring-slate-300 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select year</option>
                            {years.map(year => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                          {errors.from_year && (
                            <p className="text-red-600 text-sm mt-1">{errors.from_year}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* To Period */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-600">To</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Month
                          </label>
                          <select
                            name="to_month"
                            value={formData.to_month}
                            onChange={handleChange}
                            className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                              errors.to_month 
                                ? 'ring-red-300 focus:ring-red-500' 
                                : 'ring-slate-300 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select month</option>
                            {months.map(month => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                          {errors.to_month && (
                            <p className="text-red-600 text-sm mt-1">{errors.to_month}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Year
                          </label>
                          <select
                            name="to_year"
                            value={formData.to_year}
                            onChange={handleChange}
                            className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                              errors.to_year 
                                ? 'ring-red-300 focus:ring-red-500' 
                                : 'ring-slate-300 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select year</option>
                            {years.map(year => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                          {errors.to_year && (
                            <p className="text-red-600 text-sm mt-1">{errors.to_year}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mess Bill Amount (₹)
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.amount 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter mess bill amount"
                />
                {errors.amount && (
                  <p className="text-red-600 text-sm mt-1">{errors.amount}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rent Amount (₹)
                </label>
                <input
                  type="number"
                  name="rent_paid"
                  value={formData.rent_paid}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.rent_paid 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter rent amount"
                />
                {errors.rent_paid && (
                  <p className="text-red-600 text-sm mt-1">{errors.rent_paid}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fine Amount (₹)
                </label>
                <input
                  type="number"
                  name="fine_paid"
                  value={formData.fine_paid}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full py-3 px-4 rounded-xl ring-2 focus:outline-none transition-all ${
                    errors.fine_paid 
                      ? 'ring-red-300 focus:ring-red-500' 
                      : 'ring-slate-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter fine amount"
                />
                {errors.fine_paid && (
                  <p className="text-red-600 text-sm mt-1">{errors.fine_paid}</p>
                )}
              </div>
            </div>

            <div className="flex justify-center pt-6">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isGenerating}
                className={`px-8 py-3 rounded-xl font-semibold text-white transition-all ${
                  isGenerating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-105'
                } shadow-lg`}
              >
                {isGenerating ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Generating PDF...</span>
                  </div>
                ) : (
                  'Generate Mess Bill'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessBillGenerator;